import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { formatCurrency } from "@/lib/utility/currency-helper";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatYearsMonthDay, simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { RepairEstimateGoToHelper } from "@/lib/utility/repair-estimate.go-to-helper";
import { $t } from "@/lib/utility/t";
import repairEstimateRequestService from "@/services/mrfiktiv/services/repair-estimate-request.service";
import repairEstimateService from "@/services/mrfiktiv/services/repair-estimate.service";
import {
  MrfiktivCreateActivityLogDtoGen,
  MrfiktivCreateRequestStatusHistoryElementDtoGen,
  MrfiktivRepairEstimateViewModelGen,
  MrfiktivStatusHistoryElementViewModelGen,
  MrfiktivUpdateRepairEstimateDtoGen,
  MrfiktivUpdateRepairEstimateResponseAcceptDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { RepairEstimateDataAccessLayer } from "@/store/modules/access-layers/repair-estimate.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import VueRouter from "vue-router";
import { ActivityLog, IActivityLog } from "./activity-log.entity";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IReference, Reference } from "./reference.entity";
import { IReport, Report } from "./report.entity";
import { ISignDocument, SignDocument } from "./sign-document.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IWorkshopContractRate, WorkshopContractRate } from "./workshop-contract-rate.entity";
import { ISharedContent } from "./shared-content.entity";
import { SharedContentDataAccessLayer } from "@/store/modules/access-layers/shared-content.access-layer";
import { SharedContentModule } from "@/store/modules/shared-content.store";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import { ITag, Tag } from "./tag.entity";

@IsFilterable
class RepairEstimateBase
  implements IEntity<IRepairEstimate, MrfiktivUpdateRepairEstimateDtoGen>, MrfiktivUpdateRepairEstimateDtoGen
{
  /** Id of the repairEstimate object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.id"
  })
  id: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.repairEstimate.status",
    config: {
      items: Object.values(RequestStatusEnum).map((status) => ({
        value: status,
        text: `enums.RequestStatusEnum.${status}`
      })),
      itemValue: "value"
    }
  })
  status: RequestStatusEnum;

  /** The partnerId of the repairEstimate */
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.repairEstimate.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.repairEstimate.total"
  })
  total?: number;

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.repairEstimate.subTotal"
  })
  subTotal?: number;

  get subTotalReadable() {
    if (!this.subTotal) {
      return "";
    }
    return formatCurrency(this.subTotal, this.currency);
  }

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.repairEstimate.taxTotal"
  })
  taxTotal?: number;

  get totalReadable() {
    if (!this.total) {
      return "";
    }
    return formatCurrency(this.total, this.currency);
  }

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.number"
  })
  number?: string;

  /** date of estimate */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.repairEstimate.date"
  })
  date?: string;

  get dateFormable(): string {
    if (!this.date) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.date));
  }
  set dateFormable(date: string) {
    if (date) {
      this.date = new Date(date).toISOString();
    } else {
      this.date = "";
    }
  }

  /**
   * Returns the date in ISO format
   */
  get dateDto(): null | string {
    return this.date ? new Date(this.date).toISOString() : null;
  }

  document?: ISignDocument;

  /** Document */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.repairEstimate.documentId",
    config: {
      itemCallback: () => SignDocumentDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-document"
    }
  })
  documentId?: string;

  /**
   * ID of the related report
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.rental.reportId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-report"
    }
  })
  reportId?: string;
  report: IReport | null = null;

  /** Document */
  @FilterConfig({
    type: WorkshopContractRate
  })
  workshopRate?: IWorkshopContractRate;

  /** Document */
  lineItems: IInvoiceLine[];

  /** Buyer */
  buyer?: IInvoiceParty;

  /** Supplier */
  @FilterConfig({
    type: InvoiceParty,
    displayName: "objects.repairEstimate.seller"
  })
  seller?: IInvoiceParty;

  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  get refsCategories(): BackendResourceEnum[] {
    return [];
  }

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.repairEstimate.currency",
    config: {
      items: Object.values(CurrencyEnum)
    }
  })
  currency: CurrencyEnum;

  loading = false;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  history: MrfiktivStatusHistoryElementViewModelGen[];

  @FilterConfig({
    displayName: "objects.clearanceApproval.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees?: string[];

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.repairEstimate.tagIds",
    config: {
      itemCallback: () => TagDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ value: item, size: "small" }),
      itemValue: "id",
      component: "tag-chip"
    }
  })
  tagIds: string[] = [];

  tags: ITag[] = [];

  isDocumentLoading = false;

  isSharedContentLoading = false;

  get sharedContent(): ISharedContent[] {
    return SharedContentDataAccessLayer.entities.filter((sc) => sc.refs.some((r) => r.refId === this.id));
  }

  get lastSharedContent(): ISharedContent | undefined {
    return this.sharedContent
      .slice()
      .sort((a, b) => ((b.timestamp?.created ?? "") > (a.timestamp?.created ?? "") ? 1 : -1))[0];
  }

  get totalGross() {
    return this.lineItems.reduce((acc, item) => acc + (1 + item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get totalNet() {
    return this.lineItems.reduce((acc, item) => acc + item.amount * item.quantity, 0);
  }

  get totalTax() {
    return this.lineItems.reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get taxRates() {
    return Array.from(new Set(this.lineItems.map((line) => line.taxRate))).filter((rate) => rate !== 0);
  }

  get isOverdue() {
    if (this.date && this.overdueStates.includes(this.status)) {
      return new Date(this.date) < new Date();
    }

    return false;
  }

  get overdueStates(): [RequestStatusEnum] {
    return [RequestStatusEnum.REQUESTED];
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "primary"],
      [RequestStatusEnum.REPLIED, "green lighten-2"],
      [RequestStatusEnum.ACCEPTED, "green"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-file-document-outline"],
      [RequestStatusEnum.REPLIED, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  get canUpdate() {
    return [RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.status);
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusColor() {
    return RepairEstimateBase.statusColorMap.get(this.status);
  }

  /**
   * Icon - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusIcon() {
    return RepairEstimateBase.statusIconMap.get(this.status);
  }

  get statusReadable() {
    return $t(`enums.RequestStatusEnum.${this.status}`);
  }

  /**
   * Is the repair estimate request completed - meaning the request has been fulfilled by the receiving end- either successful or not.
   */
  get isCompleted() {
    return [
      RequestStatusEnum.REPLIED,
      RequestStatusEnum.REJECTED_BY_REQUESTED,
      RequestStatusEnum.REJECTED_BY_REQUESTING,
      RequestStatusEnum.ACCEPTED,
      RequestStatusEnum.FINISHED
    ].includes(this.status);
  }

  /**
   * Is the estimate request completed - meaning the request has been fulfilled by the receiving end- either successful or not.
   */
  get isCompletedSuccessfully() {
    return [RequestStatusEnum.REPLIED, RequestStatusEnum.ACCEPTED].includes(this.status);
  }

  get canEdit() {
    return ![RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.status);
  }

  get respondedDate() {
    if (!this.isCompleted) {
      return "";
    }
    // Find the latest status history entry with a completed-successfully status
    const completedStatuses = [RequestStatusEnum.REPLIED, RequestStatusEnum.ACCEPTED];
    const completedEntry = this.history
      ?.filter((h) => completedStatuses.includes(h.status as RequestStatusEnum))
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];

    if (completedEntry && completedEntry.at) {
      return simpleDoubleDigitDate(completedEntry.at);
    }

    // Fallback to created date from timestamp
    return this.timestamp?.createdReadable ?? "";
  }

  constructor(data: Partial<MrfiktivRepairEstimateViewModelGen | IRepairEstimate>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.total = data.total;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.number = data.number;
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line));
    this.documentId = data.documentId;
    this.reportId = data.reportId;
    this.report = data?.report ? new Report(data?.report) : null;
    this.date = data.date;
    this.buyer = data?.buyer ? new InvoiceParty(data?.buyer) : undefined;
    this.seller = new InvoiceParty(data?.seller);
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data.history ?? [];
    this.assignees = data.assignees ?? [];
    this.tagIds = data.tagIds ?? [];
    this.tags = (data.tags ?? []).map((tag) => new Tag(tag));

    if (data.document) {
      this.document = new SignDocument(data.document);
    }
  }

  map(data: Partial<MrfiktivRepairEstimateViewModelGen | IRepairEstimate>): void {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.total = data.total;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.number = data.number;
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line));
    this.documentId = data.documentId;
    this.reportId = data.reportId;
    this.report = data?.report ? new Report(data?.report) : null;
    this.date = data.date;
    this.buyer = data?.buyer ? new InvoiceParty(data?.buyer) : undefined;
    this.seller = new InvoiceParty(data?.seller);
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data.history ?? [];
    this.assignees = data.assignees ?? [];
    this.tagIds = data.tagIds ?? [];
    this.tags = (data.tags ?? []).map((tag) => new Tag(tag));

    if (data.document) {
      this.document = new SignDocument(data.document);
    }
  }

  /**
   * Calculates all the line items for a given taxRate
   * @param taxRate the taxRate to calculate tax for
   * @returns
   */
  calcItemsPerTaxRate(taxRate: number): number {
    return this.lineItems
      .filter((l) => l.taxRate === taxRate)
      .reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  async create(): Promise<this> {
    this.loading = true;
    try {
      const res = await repairEstimateService.create(this.partnerId, {
        date: this.dateDto,
        documentId: this.documentId,
        reportId: this.reportId,
        lineItems: this.lineItems.map((li) => li.dto),
        number: this.number,
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        workshopRate: this.workshopRate,
        refs: Reference.filterDuplicates(this.refs),
        currency: this.currency,
        status: this.status,
        assignees: this.assignees
      });

      this.map(res);
      RepairEstimateDataAccessLayer.set(this);

      await new ActivityLog().createCreationActivity({
        partnerId: this.partnerId,
        source: { refId: this.id, refType: BackendResourceEnum.REPAIR_ESTIMATE }
      });

      await this.fetchReportAndVehicle();
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    await Promise.all([this.addCreateActivitiesOnReferences()]);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await repairEstimateService.getOne(this.partnerId, this.id);
      this.map(res);
      RepairEstimateDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      RepairEstimateDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateRepairEstimateDtoGen): Promise<this> {
    const res = await repairEstimateService.update(this.partnerId, this.id, dto);

    this.map(res);
    RepairEstimateDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      await this.updatePartial({
        date: this.dateDto,
        documentId: this.documentId,
        reportId: this.reportId,
        lineItems: this.lineItems.map((line) => line.dto),
        number: this.number,
        workshopRate: this.workshopRate,
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        refs: Reference.filterDuplicates(this.refs),
        status: this.status,
        currency: this.currency,
        assignees: this.assignees,
        tagIds: this.tags.map((tag) => tag.id)
      });
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async delete(): Promise<void> {
    this.loading = true;
    try {
      const res = await repairEstimateService.delete(this.partnerId, this.id);

      this.map(res);
      RepairEstimateDataAccessLayer.delete(this);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
  }

  goTo(router: VueRouter) {
    return {
      table: () => new RepairEstimateGoToHelper(router).goToRepairEstimateTable({ partnerId: this.partnerId }),
      detail: () =>
        new RepairEstimateGoToHelper(router).goToRepairEstimateDetail({
          partnerId: this.partnerId,
          repairEstimateId: this.id
        })
    };
  }

  async accept(): Promise<this> {
    this.loading = true;
    try {
      const updated = await repairEstimateService.updateStatus(this.partnerId, this.id, RequestStatusEnum.ACCEPTED);

      this.map(updated);

      await this.updateStatusLog();
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  /**
   * Set the repair estimate status to FINISHED
   */
  async finish(): Promise<this> {
    this.loading = true;
    try {
      const updated = await repairEstimateService.updateStatus(this.partnerId, this.id, RequestStatusEnum.FINISHED);
      this.map(updated);
      await this.updateStatusLog();
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
    return this;
  }

  async reject(): Promise<this> {
    this.loading = true;
    try {
      const updated = await repairEstimateService.updateStatus(
        this.partnerId,
        this.id,
        RequestStatusEnum.REJECTED_BY_REQUESTING
      );

      this.map(updated);

      await this.updateStatusLog();
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  /**
   * Accept the repair estimate request
   */
  async respondAccept(token: string, data: MrfiktivUpdateRepairEstimateResponseAcceptDtoGen): Promise<this> {
    this.loading = true;
    try {
      const response = await repairEstimateRequestService.respondAccept(this.partnerId, this.id, token, data);

      this.map(response);

      RepairEstimateDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      throw e;
    } finally {
      this.loading = false;
    }

    return this;
  }

  /**
   * Accept the repair estimate request
   */
  async respondReject(token: string, data: MrfiktivCreateRequestStatusHistoryElementDtoGen): Promise<this> {
    this.loading = true;
    try {
      const response = await repairEstimateRequestService.respondReject(this.partnerId, this.id, token, data);

      this.map(response);

      RepairEstimateDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      throw e;
    } finally {
      this.loading = false;
    }

    return this;
  }

  /**
   * Adds activity logs celebrating the creation of the repair estimate
   * @returns the created activity logs
   */
  addCreateActivitiesOnReferences(): Promise<IActivityLog[]> {
    const sources = [...this.refs];
    if (this.documentId) {
      sources.push({
        refType: BackendResourceEnum.DOCUMENT,
        refId: this.documentId
      });
    }
    if (
      this.reportId &&
      !sources.some((ref) => ref.refType === BackendResourceEnum.REPORT && ref.refId === this.reportId)
    ) {
      sources.push(
        new Reference({
          refType: BackendResourceEnum.REPORT,
          refId: this.reportId
        })
      );
    }
    const target = {
      refType: BackendResourceEnum.REPAIR_ESTIMATE,
      refId: this.id
    };

    const activityAsync = [];

    for (const source of sources) {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: source,
        target: [target],
        actionType: ActionEnum.CREATE,
        activity: ActivityTypeEnum.CREATE_REF
      };

      activityAsync.push(
        new ActivityLog({
          partnerId: this.partnerId,
          ...data
        }).create()
      );
    }

    return Promise.all(activityAsync);
  }

  /**
   * Create an activity log for assignee changes
   * @param newAssignees The ids of the new assignees of the ticket
   * @param activityType Whether the assignee was added or removed
   */
  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: ResourceEnum.REPAIR_ESTIMATE,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }

  async fetchReportAndVehicle() {
    this.report = null;

    const reportId = this.reportId;
    if (!reportId) {
      return;
    }

    this.report =
      ReportDataAccessLayer.get(reportId) ||
      (await new Report({
        id: reportId,
        partnerId: this.partnerId
      }).fetch()) ||
      null;

    await this.report?.fetchVehicle();
  }

  setDefaultLineItems() {
    this.lineItems = [
      new InvoiceLine({ title: $t("objects.repairEstimate.default.wages").toString(), taxRate: 19, quantity: 1 }),
      new InvoiceLine({ title: $t("objects.repairEstimate.default.paintwork").toString(), taxRate: 19, quantity: 1 }),
      new InvoiceLine({ title: $t("objects.repairEstimate.default.spareParts").toString(), taxRate: 19, quantity: 1 }),
      new InvoiceLine({
        title: $t("objects.repairEstimate.default.smallSpareParts").toString(),
        taxRate: 19,
        quantity: 1
      }),
      new InvoiceLine({ title: $t("objects.repairEstimate.default.other").toString(), taxRate: 19, quantity: 1 })
    ];
    return this;
  }

  async fetchDocument() {
    if (!this.documentId) {
      this.document = undefined;

      return;
    }
    try {
      this.isDocumentLoading = true;
      this.document = await new SignDocument({ id: this.documentId, partnerId: this.partnerId }).fetch();
    } finally {
      this.isDocumentLoading = false;
    }
  }

  /**
   *
   * @param original containing the original values to compare against
   */
  async createManagePropertiesLog(original: IRepairEstimate) {
    const changedKeys: string[] = [];
    if (this.number !== original.number) {
      changedKeys.push("objects.repairEstimate.number");
    }
    if (JSON.stringify(this.lineItems?.map((e) => e.dto)) !== JSON.stringify(original.lineItems?.map((e) => e.dto))) {
      changedKeys.push("objects.repairEstimate.lineItems");
    }
    if (this.documentId !== original.documentId) {
      changedKeys.push("objects.repairEstimate.documentId");
    }
    if (this.currency !== original.currency) {
      changedKeys.push("objects.repairEstimate.currency");
    }

    if (changedKeys.length) {
      await new ActivityLog().createManagePropertiesLog({
        keys: changedKeys,
        source: { refId: this.id, refType: ResourceEnum.REPAIR_ESTIMATE },
        partnerId: this.partnerId
      });
    }

    if (this.status !== original.status) {
      await this.updateStatusLog();
    }

    return this;
  }

  async updateStatusLog() {
    await new ActivityLog().updateStatusLog({
      state: this.status,
      source: { refId: this.id, refType: ResourceEnum.REPAIR_ESTIMATE },
      partnerId: this.partnerId,
      comment: $t(`enums.RequestStatusEnum.${this.status}`)
    });

    return this;
  }

  async fetchSharedContent() {
    this.isSharedContentLoading = true;
    try {
      await SharedContentModule.fetchByRef({ partnerId: this.partnerId, refId: this.id });
    } finally {
      this.isSharedContentLoading = false;
    }
  }
}

type IRepairEstimate = RepairEstimateBase;
const RepairEstimate = Filter.createForClass(RepairEstimateBase);

export { IRepairEstimate, RepairEstimate };
